import { call, ForkEffect, put, takeLatest } from "redux-saga/effects";
import {
  authActions,
  loginFailureAction,
  loginSuccessAction,
  logoutFailureAction,
  registerUserAfterOtpVerificationActionFailure,
  registerUserAfterOtpVerificationActionSuccess,
} from "@common/redux/auth/actions";
import { AuthRepository } from "@common/domain/repositories/AuthRepository";
import {
  IAuthData,
  ILoginAction,
  IProfileAction,
  IRegisterUserActionFailure,
  IRegisterUserDataAfterOtpVerificationAction,
  IResetPasswordAction,
  ISendOtpAction,
  IVerifyOtpAction,
  IVerifyOtpActionResponse,
} from "@common/redux/auth/interface";
import {
  setCurrentLoggedinUserIdAction,
  setLoggedInUserProfileCompleted,
  updateProfileSuccessAction,
} from "@common/redux/profile/actions";
import { StorageKeys } from "@utils/LocalStorage";
import { isProfileDataCompleted } from "@common/redux/profile/utils";
import { LoginSourceType } from "./utils";
import { IProfileData } from "../profile/interface";
import { IApiClientErrorDetails } from "@common/network/apiClientError";
import { errorCodes } from "@common/network/utils";
import { ERR_ALREADY_REGISTERED } from "@common/network/constants";
import { getParseData } from "@common/utils/common";
import sendAnalytics from "@analytics/sendAnalytics";
import analyticsAuth from "@analytics/events/auth.json";
import userService from "@erelego/design-system/components/atoms/login/keycloak";

function* afterAuthenticationSaga(action: IProfileAction) {
  const response = action.payload.profileData;
  yield put(setCurrentLoggedinUserIdAction(response?.userId));
  yield put(updateProfileSuccessAction(response));
  yield put(setLoggedInUserProfileCompleted(isProfileDataCompleted(response)));
}

function* loginSaga(action: ILoginAction) {
  try {
    const { loginPayloadData } = action?.payload;
    const { loginType, emailOrMobile } = loginPayloadData;
    let payload;
    if (loginType == LoginSourceType.GOOGLE) {
      payload = {
        loginType: loginType,
        tokenId: loginPayloadData.tokenId,
      };
    } else {
      payload = {
        loginType: loginType,
        emailOrMobile: emailOrMobile,
        password: loginPayloadData.password,
      };
    }
    const response = yield call(AuthRepository.login, payload);

    const { authToken } = response;

    const authData: IAuthData = {
      sessionToken: authToken,
      loginType,
    };
    const responseData: IProfileData = response.data;
    // yield afterAuthenticationSaga(responseData);
    yield put(loginSuccessAction(authData));
    sendAnalytics(analyticsAuth.loginSuccess);
    localStorage.setItem(StorageKeys.TOKEN, response.authToken);
  } catch (e) {
    sendAnalytics(analyticsAuth.loginFailure, { error: e.message });
    yield put(loginFailureAction(e.message));
  }
}

function* logoutSaga() {
  try {
    userService.logout().then(() => {
      localStorage.clearAll();
      userService.doLogin();
    });
  } catch (e) {
    yield put(logoutFailureAction(e.message));
  }
}

function* sendOtpSaga(action: ISendOtpAction) {
  const { onFailureSendOtp, onSuccessSendOtp, requesterData } = action.payload;
  try {
    const response = yield call(AuthRepository.sendOtp, requesterData);
    onSuccessSendOtp(response);
  } catch (e) {
    onFailureSendOtp(e.message);
  }
}

function* verifyOtpSaga(action: IVerifyOtpAction) {
  const { onFailureVerifyOtp, onSuccessVerifyOtp, requesterData } =
    action.payload;
  let otpErrorData;
  try {
    const response = yield call(AuthRepository.verifyOtp, requesterData);

    response.data.forEach((data: IVerifyOtpActionResponse) => {
      switch (data.requesterType) {
        case LoginSourceType.EMAIL:
          if (data.status == "Failure") {
            otpErrorData = {
              ...otpErrorData,
              emailOrMobileOtp: "Incorrect OTP, please enter a valid OTP.",
            };
          }
          break;
        case LoginSourceType.MOBILE:
          if (data.status == "Failure") {
            otpErrorData = {
              ...otpErrorData,
              emailOrMobileOtp: "Incorrect OTP, please enter a valid OTP.",
            };
          }
          break;
      }
    });
    if (otpErrorData) {
      throw otpErrorData;
    } else {
      onSuccessVerifyOtp(response.data);
    }
  } catch (error) {
    if (otpErrorData) {
      onFailureVerifyOtp(error);
    } else {
      onFailureVerifyOtp(error.message);
    }
  }
}

function* resetPasswordSaga(action: IResetPasswordAction) {
  const { onSuccessPasswordReset, onFailurePasswordReset, requesterData } =
    action.payload;
  try {
    const response = yield call(AuthRepository.resetPassword, requesterData);
    onSuccessPasswordReset(response);
  } catch (error) {
    onFailurePasswordReset(error.message);
  }
}

function* registerUserSaga(
  action: IRegisterUserDataAfterOtpVerificationAction
) {
  try {
    const response = yield call(
      AuthRepository.registerUserAfterOtpVerification,
      action.payload
    );
    const { authToken, loginType } = response;
    const authData: IAuthData = {
      sessionToken: authToken,
      loginType,
    };
    const responseData: IProfileData = response.data;
    // yield afterAuthenticationSaga(responseData);
    yield put(registerUserAfterOtpVerificationActionSuccess(authData));
    sendAnalytics(
      loginType === LoginSourceType.GOOGLE
        ? analyticsAuth.googleSignupSuccess
        : analyticsAuth.signupSuccess
    );
  } catch (e) {
    const loginType = action.payload?.loginType;
    const error: IApiClientErrorDetails = getParseData(e);
    let updatedError: IRegisterUserActionFailure = {
      errorCode: error?.code,
      errorMessage: error?.message,
    };
    if (errorCodes.ERR_ALREADY_REGISTERED === error?.code) {
      updatedError = {
        errorCode: error?.code,
        errorMessage: ERR_ALREADY_REGISTERED,
      };
    }
    sendAnalytics(
      loginType === LoginSourceType.GOOGLE
        ? analyticsAuth.signupFailure
        : analyticsAuth.googleSignupFailure,
      {
        error: updatedError.errorMessage,
      }
    );
    yield put(registerUserAfterOtpVerificationActionFailure(updatedError));
  }
}

export function* watchAuth(): Generator<ForkEffect> {
  yield takeLatest(authActions.LOGIN, loginSaga);
  yield takeLatest(authActions.LOGOUT, logoutSaga);
  yield takeLatest(authActions.SEND_OTP, sendOtpSaga);
  yield takeLatest(authActions.VERIFY_OTP, verifyOtpSaga);
  yield takeLatest(authActions.RESET_PASSWORD, resetPasswordSaga);
  yield takeLatest(authActions.UPDATE_PROFILE_DATA, afterAuthenticationSaga);
  yield takeLatest(
    authActions.REGISTER_USER_AFTER_OTP_VERIFICATION,
    registerUserSaga
  );
}
